// Simple Video Room Practice - Just video + chat to test keyboard behavior
import React, { useState, useRef, useCallback } from 'react';
import { X, Send } from 'lucide-react';

interface Message {
  id: string;
  sender: string;
  text: string;
  timestamp: number;
}

interface SimpleVideoRoomPracticeProps {
  onClose: () => void;
}

export default function SimpleVideoRoomPractice({ onClose }: SimpleVideoRoomPracticeProps) {
  const [messages, setMessages] = useState<Message[]>([
    { id: '1', sender: 'System', text: 'Welcome! Try typing to see keyboard behavior.', timestamp: Date.now() }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keyboard Detection
  React.useEffect(() => {
    const checkKeyboard = () => {
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const isKeyboardOpen = viewportHeight < windowHeight * 0.75;
        setKeyboardVisible(isKeyboardOpen);
        console.log('[Practice] Keyboard visible:', isKeyboardOpen, 'Viewport:', viewportHeight, 'Window:', windowHeight);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', checkKeyboard);
      window.visualViewport.addEventListener('scroll', checkKeyboard);
      checkKeyboard();
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', checkKeyboard);
        window.visualViewport.removeEventListener('scroll', checkKeyboard);
      }
    };
  }, []);

  // Auto-scroll chat to bottom
  React.useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = useCallback(() => {
    if (!inputValue.trim()) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      sender: 'You',
      text: inputValue.trim(),
      timestamp: Date.now(),
    };

    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    console.log('[Practice] Sent:', newMessage.text);
  }, [inputValue]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 to-purple-900 flex flex-col z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/30 border-b border-white/10 flex-shrink-0">
        <h2 className="text-white font-bold text-lg">🎥 Practice Room</h2>
        <button
          onClick={onClose}
          className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Debug Info */}
      <div className="px-4 py-2 bg-yellow-500/10 border-b border-yellow-500/30 text-yellow-200 text-xs flex-shrink-0">
        🔍 Keyboard: {keyboardVisible ? 'OPEN ⌨️' : 'CLOSED'} | 
        Viewport: {window.visualViewport?.height || 0}px | 
        Window: {window.innerHeight}px
      </div>

      {/* Full-Screen Video with Chat Overlay - FIXED HEIGHT */}
      <div className="flex-1 relative" style={{ minHeight: 0 }}>
        {/* Video Background (Full Screen) */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 mx-auto mb-4 flex items-center justify-center text-white text-3xl font-bold">
              👤
            </div>
            <p className="text-white/60 text-lg font-semibold">Video Window</p>
            <p className="text-white/40 text-sm mt-2">(Should stay stable)</p>
          </div>
        </div>

        {/* Transparent Chat Overlay - Bottom Right */}
        <div className="absolute bottom-20 right-4 w-80 max-h-96 bg-black/60 backdrop-blur-xl border border-white/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          <div className="px-3 py-2 border-b border-white/30 bg-purple-900/30 flex-shrink-0">
            <h3 className="text-white font-semibold text-sm">💬 Chat</h3>
          </div>

          {/* Messages - Scrollable */}
          <div 
            ref={chatRef}
            className="flex-1 overflow-y-auto p-3 space-y-2"
            style={{ maxHeight: '300px' }}
          >
            {messages.map((msg) => (
              <div key={msg.id} className="bg-white/10 backdrop-blur-sm rounded-lg p-2">
                <div className="text-xs text-purple-300 font-bold">{msg.sender}</div>
                <div className="text-sm text-white mt-1">{msg.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Input Box - ABSOLUTELY POSITIONED, SHORTER INPUT */}
      <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-r from-purple-900/95 to-indigo-900/95 backdrop-blur-xl border-t border-purple-500/30 flex items-center gap-2 z-10">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={() => setKeyboardVisible(true)}
          onBlur={() => setTimeout(() => setKeyboardVisible(false), 100)}
          placeholder="Type message..."
          className="flex-1 bg-gray-800/70 text-white placeholder-gray-400 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 backdrop-blur-sm"
          style={{ maxWidth: 'calc(100% - 60px)' }}
        />
        <button
          onClick={handleSendMessage}
          disabled={!inputValue.trim()}
          className="w-10 h-10 flex-shrink-0 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
